import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Paper,
  Divider,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  CalendarToday as CalendarIcon,
  LocationOn as LocationIcon,
  SportsTennis as SportsIcon,
  School as SchoolIcon,
  Phone as PhoneIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  VerifiedUser as VerifiedIcon,
  CheckCircle as CheckCircleIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { Player } from '@services/adminService';
import { GenderType, SkillLevel } from '@types/index';
import { toast } from 'react-toastify';
import AcademySearch from '@components/AcademySearch';
import { SPORTS } from '@utils/sports';

interface PlayerProfileProps {
  player: Player;
  onUpdate?: (updatedPlayer: Player) => void;
}

// Normalize player from API (flat: firstName) or legacy (nested: user.firstName)
const getPlayerDisplay = (p: Player | undefined) => ({
  firstName: p?.user?.firstName ?? (p as any)?.firstName ?? '',
  lastName: p?.user?.lastName ?? (p as any)?.lastName ?? '',
  phone: p?.phone ?? '',
  dateOfBirth: p?.dateOfBirth,
  gender: p?.gender ?? GenderType.MALE,
  level: p?.level ?? (p as any)?.skillLevel ?? SkillLevel.BEGINNER,
  country: p?.country ?? '',
  primarySport: p?.primarySport ?? '',
  secondarySport: p?.secondarySport ?? '',
  academyId: (p as any)?.academyId ?? null,
  academyName: p?.academyName ?? '',
  sparringOptIn: (p as any)?.sparringOptIn === true,
});

const PlayerProfile: React.FC<PlayerProfileProps> = ({ player: initialPlayer, onUpdate }) => {
  const display = getPlayerDisplay(initialPlayer);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [player, setPlayer] = useState<Player>(initialPlayer);
  const [verificationStatus, setVerificationStatus] = useState<{
    email: string;
    isEmailVerified: boolean;
    isPhoneVerified: boolean;
    phone: string | null;
  } | null>(null);
  const [sendingEmailVerification, setSendingEmailVerification] = useState(false);
  const [sendingOTP, setSendingOTP] = useState(false);
  const [otpDialogOpen, setOtpDialogOpen] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [verifyingOTP, setVerifyingOTP] = useState(false);
  const [formData, setFormData] = useState({
    first_name: display.firstName,
    last_name: display.lastName,
    phone: display.phone,
    date_of_birth: display.dateOfBirth ? new Date(display.dateOfBirth).toISOString().split('T')[0] : '',
    gender: display.gender,
    skill_level: display.level,
    country: display.country,
    primary_sport: display.primarySport,
    secondary_sport: display.secondarySport,
    academy_id: display.academyId,
    academy_name: display.academyName,
    sparring_opt_in: display.sparringOptIn,
  });
  
  const [academies, setAcademies] = useState<Array<{id: string, name: string}>>([]);
  
  // Load academies for dropdown (fallback)
  useEffect(() => {
    const loadAcademies = async () => {
      try {
        const response = await fetch('/api/academies/search?status=active');
        const data = await response.json();
        if (data.success && data.data) {
          setAcademies(data.data.map((academy: any) => ({
            id: academy.id,
            name: academy.name
          })));
        }
      } catch (err) {
        console.error('Error loading academies:', err);
      }
    };
    loadAcademies();
  }, []);

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Not specified';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getAge = (dateOfBirth: string) => {
    if (!dateOfBirth) return 'Not specified';
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const handleEditProfile = () => {
    setEditing(true);
    setFormData({
      first_name: player.user?.firstName ?? (player as any).firstName ?? '',
      last_name: player.user?.lastName ?? (player as any).lastName ?? '',
      phone: player.phone || '',
      date_of_birth: player.dateOfBirth ? new Date(player.dateOfBirth).toISOString().split('T')[0] : '',
      gender: player.gender || GenderType.MALE,
      skill_level: player.level || SkillLevel.BEGINNER,
      country: player.country || '',
      primary_sport: player.primarySport || '',
      secondary_sport: player.secondarySport || '',
      academy_id: (player as any).academyId || null,
      academy_name: player.academyName || '',
      sparring_opt_in: (player as any).sparringOptIn === true,
    });
  };

  const handleSaveProfile = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`/api/players/${player.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName: formData.first_name,
          lastName: formData.last_name,
          dateOfBirth: formData.date_of_birth,
          gender: formData.gender,
          level: formData.skill_level,
          phone: formData.phone,
          country: formData.country,
          primarySport: formData.primary_sport,
          secondarySport: formData.secondary_sport,
          academyId: formData.academy_id,
          academyName: formData.academy_name,
          sparringOptIn: formData.sparring_opt_in,
          // ageCategory will be calculated on backend from dateOfBirth
        }),
      });

      const data = await response.json();

      if (data.success) {
        // Reload player data from backend to get updated ageCategory and other fields
        try {
          const playerResponse = await fetch(`/api/players/me?userId=${player.userId}`);
          const playerData = await playerResponse.json();
          
          if (playerData.success && playerData.data) {
            const d = playerData.data;
            setPlayer({
              ...d,
              userId: d.userId || player.userId,
              user: {
                id: d.userId || player.user?.id,
                firstName: d.firstName ?? player.user?.firstName,
                lastName: d.lastName ?? player.user?.lastName,
                email: d.email ?? player.user?.email,
                role: d.role ?? player.user?.role,
                isActive: d.isActive ?? player.user?.isActive ?? true,
              },
            });
            setEditing(false);
            toast.success('Profile updated successfully!');
            if (onUpdate) {
              onUpdate(playerData.data);
            }
          } else {
            // Fallback to local update if reload fails
            const updatedPlayer = {
              ...player,
              user: {
                ...(player.user || {}),
                firstName: formData.first_name,
                lastName: formData.last_name,
              },
              phone: formData.phone,
              dateOfBirth: formData.date_of_birth,
              gender: formData.gender,
              level: formData.skill_level,
              country: formData.country,
              primarySport: formData.primary_sport,
              secondarySport: formData.secondary_sport,
              academyName: formData.academy_name,
              sparringOptIn: formData.sparring_opt_in,
            };
            setPlayer(updatedPlayer);
            setEditing(false);
            toast.success('Profile updated successfully!');
            if (onUpdate) {
              onUpdate(updatedPlayer);
            }
          }
        } catch (reloadError) {
          console.error('Error reloading player data:', reloadError);
          // Still update locally
          const updatedPlayer = {
            ...player,
            user: {
              ...(player.user || {}),
              firstName: formData.first_name,
              lastName: formData.last_name,
            },
            phone: formData.phone,
            dateOfBirth: formData.date_of_birth,
            gender: formData.gender,
            level: formData.skill_level,
            country: formData.country,
            primarySport: formData.primary_sport,
            secondarySport: formData.secondary_sport,
            academyName: formData.academy_name,
            sparringOptIn: formData.sparring_opt_in,
          };
          setPlayer(updatedPlayer);
          setEditing(false);
          toast.success('Profile updated successfully!');
          if (onUpdate) {
            onUpdate(updatedPlayer);
          }
        }
      } else {
        setError(data.message || 'Failed to update profile');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update profile');
      console.error('Error saving profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditing(false);
    setFormData({
      first_name: player.user?.firstName ?? (player as any).firstName ?? '',
      last_name: player.user?.lastName ?? (player as any).lastName ?? '',
      phone: player.phone || '',
      date_of_birth: player.dateOfBirth ? new Date(player.dateOfBirth).toISOString().split('T')[0] : '',
      gender: player.gender || GenderType.MALE,
      skill_level: player.level || SkillLevel.BEGINNER,
      country: player.country || '',
      primary_sport: player.primarySport || '',
      secondary_sport: player.secondarySport || '',
      academy_id: (player as any).academyId || null,
      academy_name: player.academyName || '',
      sparring_opt_in: (player as any).sparringOptIn === true,
    });
  };

  useEffect(() => {
    if (!initialPlayer) return;
    const normalized = initialPlayer.user
      ? initialPlayer
      : {
          ...initialPlayer,
          user: {
            id: (initialPlayer as any).userId,
            firstName: (initialPlayer as any).firstName,
            lastName: (initialPlayer as any).lastName,
            email: (initialPlayer as any).email,
            role: (initialPlayer as any).role,
            isActive: (initialPlayer as any).isActive ?? true,
          },
        };
    setPlayer(normalized);
    loadVerificationStatus();
  }, [initialPlayer]);

  // Automatically refresh verification status - updates UI immediately when email is verified
  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;

    const checkAndRefresh = () => {
      loadVerificationStatus();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Refresh immediately when page becomes visible
        checkAndRefresh();
        // Start periodic check (every 3 seconds) if email is not verified
        if (!verificationStatus?.isEmailVerified) {
          intervalId = setInterval(checkAndRefresh, 3000);
        }
      } else {
        // Stop checking when page is hidden
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
      }
    };

    const handleFocus = () => {
      // Refresh when window regains focus
      checkAndRefresh();
    };

    // Start periodic check if email is not verified and page is visible
    if (document.visibilityState === 'visible' && !verificationStatus?.isEmailVerified) {
      intervalId = setInterval(checkAndRefresh, 3000);
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [verificationStatus?.isEmailVerified]);

  const loadVerificationStatus = async () => {
    try {
      const userId = initialPlayer.user?.id || initialPlayer.id;
      if (!userId) return;
      
      const response = await fetch(`/api/auth/verification-status/${userId}`);
      const data = await response.json();
      if (data.success) {
        setVerificationStatus(data.data);
      }
    } catch (err) {
      console.error('Error loading verification status:', err);
    }
  };

  const handleSendEmailVerification = async () => {
    try {
      setSendingEmailVerification(true);
      // Get user ID - try user.id first, then userId, then player.id as fallback
      const userId = player.user?.id || (player as any).userId || player.id;
      console.log('Sending email verification for userId:', userId, 'player:', player);
      const response = await fetch('/api/auth/send-email-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId }),
      });
      const data = await response.json();
      if (data.success) {
        toast.success(data.message || 'Verification email sent!');
        if (data.verificationLink && process.env.NODE_ENV === 'development') {
          console.log('Verification link:', data.verificationLink);
        }
      } else {
        toast.error(data.message || 'Failed to send verification email');
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to send verification email');
    } finally {
      setSendingEmailVerification(false);
    }
  };

  const handleSendOTP = async () => {
    try {
      setSendingOTP(true);
      const userId = player.user?.id || player.id;
      const phone = player.phone || verificationStatus?.phone;
      if (!phone) {
        toast.error('Please add a phone number in your profile first');
        return;
      }
      const response = await fetch('/api/auth/send-phone-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, phone }),
      });
      const data = await response.json();
      if (data.success) {
        toast.success(data.message || 'OTP sent to your phone!');
        setOtpDialogOpen(true);
        if (data.otpCode && process.env.NODE_ENV === 'development') {
          console.log('OTP Code:', data.otpCode);
        }
      } else {
        toast.error(data.message || 'Failed to send OTP');
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to send OTP');
    } finally {
      setSendingOTP(false);
    }
  };

  const handleVerifyOTP = async () => {
    try {
      setVerifyingOTP(true);
      const userId = player.user?.id || player.id;
      const phone = player.phone || verificationStatus?.phone;
      if (!phone) {
        toast.error('Phone number not found');
        return;
      }
      const response = await fetch('/api/auth/verify-phone-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, phone, otpCode }),
      });
      const data = await response.json();
      if (data.success) {
        toast.success(data.message || 'Phone verified successfully!');
        setOtpDialogOpen(false);
        setOtpCode('');
        loadVerificationStatus();
      } else {
        toast.error(data.message || 'Invalid OTP code');
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to verify OTP');
    } finally {
      setVerifyingOTP(false);
    }
  };

  if (!player?.id) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info">Loading profile...</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon />
            My Profile
          </Typography>
          <Typography variant="body1" color="text.secondary">
            View and manage your personal information
          </Typography>
        </Box>
        {!editing && (
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={handleEditProfile}
            sx={{ minWidth: 150 }}
          >
            Edit Profile
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Edit Profile Dialog */}
      <Dialog open={editing} onClose={handleCancelEdit} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EditIcon />
            Edit Profile
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="First Name"
                  fullWidth
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Last Name"
                  fullWidth
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Phone"
                  fullWidth
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Date of Birth"
                  type="date"
                  fullWidth
                  value={formData.date_of_birth}
                  onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Gender"
                  fullWidth
                  value={formData.gender}
                  onChange={(e) => setFormData({ ...formData, gender: e.target.value as GenderType })}
                  required
                >
                  <MenuItem value={GenderType.MALE}>Male</MenuItem>
                  <MenuItem value={GenderType.FEMALE}>Female</MenuItem>
                  <MenuItem value={GenderType.OTHER}>Other</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Skill Level"
                  fullWidth
                  value={formData.skill_level}
                  onChange={(e) => setFormData({ ...formData, skill_level: e.target.value as SkillLevel })}
                  required
                >
                  <MenuItem value={SkillLevel.BEGINNER}>Beginner</MenuItem>
                  <MenuItem value={SkillLevel.INTERMEDIATE}>Intermediate</MenuItem>
                  <MenuItem value={SkillLevel.ADVANCED}>Advanced</MenuItem>
                  <MenuItem value={SkillLevel.PROFESSIONAL}>Professional</MenuItem>
                </TextField>
              </Grid>
              
              {/* Sports & Training Fields */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                  Sports & Training Information
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Country"
                  fullWidth
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Primary Sport"
                  fullWidth
                  value={formData.primary_sport}
                  onChange={(e) => setFormData({ ...formData, primary_sport: e.target.value })}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {SPORTS.map((sport) => (
                    <MenuItem key={sport} value={sport}>
                      {sport}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Secondary Sport"
                  fullWidth
                  value={formData.secondary_sport}
                  onChange={(e) => setFormData({ ...formData, secondary_sport: e.target.value })}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {SPORTS.map((sport) => (
                    <MenuItem key={sport} value={sport}>
                      {sport}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  select
                  label="Open to Sparring"
                  fullWidth
                  value={formData.sparring_opt_in ? 'yes' : 'no'}
                  onChange={(e) => setFormData({ ...formData, sparring_opt_in: e.target.value === 'yes' })}
                >
                  <MenuItem value="no">No</MenuItem>
                  <MenuItem value="yes">Yes</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12}>
                <AcademySearch
                  value={formData.academy_id}
                  onChange={(academyId, academyName) => {
                    setFormData({
                      ...formData,
                      academy_id: academyId,
                      academy_name: academyName || '',
                    });
                  }}
                  country={formData.country}
                  label="Select Academy"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEdit} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveProfile} 
            variant="contained" 
            startIcon={<SaveIcon />}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      <Grid container spacing={3}>
        {/* Personal Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon />
                Personal Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Full Name</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {player.user?.firstName ?? (player as any).firstName} {player.user?.lastName ?? (player as any).lastName}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Player ID</Typography>
                  <Chip 
                    label={player.playerIdCode || player.id || 'N/A'} 
                    color="primary" 
                    variant="filled"
                    sx={{ 
                      fontWeight: 'bold', 
                      fontSize: '0.95rem',
                      py: 2.5,
                      minHeight: 40
                    }}
                  />
                </Box>
                
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                      <EmailIcon fontSize="small" />
                      {player.user?.email ?? (player as any).email}
                    </Typography>
                    {verificationStatus?.isEmailVerified ? (
                      <Chip
                        icon={<CheckCircleIcon />}
                        label="Verified"
                        color="success"
                        size="small"
                      />
                    ) : (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<SendIcon />}
                        onClick={handleSendEmailVerification}
                        disabled={sendingEmailVerification}
                        sx={{ minWidth: 120 }}
                      >
                        {sendingEmailVerification ? 'Sending...' : 'Verify'}
                      </Button>
                    )}
                  </Box>
                </Box>
                
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Date of Birth</Typography>
                  <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarIcon fontSize="small" />
                    {formatDate(player.dateOfBirth)}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Age</Typography>
                  <Typography variant="body1">
                    {getAge(player.dateOfBirth)} years old
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Gender</Typography>
                  <Chip 
                    label={player.gender ? player.gender.charAt(0).toUpperCase() + player.gender.slice(1) : 'Not specified'} 
                    color="secondary" 
                    size="small"
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Sports & Training Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SportsIcon />
                Sports & Training
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Skill Level</Typography>
                  <Chip 
                    label={player.level ? player.level.charAt(0).toUpperCase() + player.level.slice(1) : 'Not specified'} 
                    color="success" 
                    size="small"
                  />
                </Box>
                
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Age Category</Typography>
                  <Chip 
                    label={player.ageCategory || 'Not specified'} 
                    color="info" 
                    size="small"
                  />
                </Box>
                
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Primary Sport</Typography>
                  <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SportsIcon fontSize="small" />
                    {player.primarySport || 'Not specified'}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Secondary Sport</Typography>
                  <Typography variant="body1">
                    {player.secondarySport || 'Not specified'}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Sparring Preference</Typography>
                  <Chip
                    label={(player as any).sparringOptIn ? 'Open to Sparring' : 'Not Open to Sparring'}
                    color={(player as any).sparringOptIn ? 'warning' : 'default'}
                    size="small"
                  />
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Phone</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                      <PhoneIcon fontSize="small" />
                      {player.phone || verificationStatus?.phone || 'Not specified'}
                    </Typography>
                    {player.phone || verificationStatus?.phone ? (
                      verificationStatus?.isPhoneVerified ? (
                        <Chip
                          icon={<CheckCircleIcon />}
                          label="Verified"
                          color="success"
                          size="small"
                        />
                      ) : (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<SendIcon />}
                          onClick={handleSendOTP}
                          disabled={sendingOTP}
                          sx={{ minWidth: 120 }}
                        >
                          {sendingOTP ? 'Sending...' : 'Verify'}
                        </Button>
                      )
                    ) : null}
                  </Box>
                </Box>
                
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Country</Typography>
                  <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationIcon fontSize="small" />
                    {player.country || 'Not specified'}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Academy</Typography>
                  <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SchoolIcon fontSize="small" />
                    {player.academyName || 'Personal Training'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Parent/Guardian Information - Show if any parent data exists */}
        {(() => {
          const hasParentData = (player as any).registeredByParent || 
                                (player as any).parentEmail || 
                                (player as any).parentPhone || 
                                (player as any).fatherName || 
                                (player as any).motherName ||
                                (player as any).parentCountry;
          console.log('PlayerProfile - Checking parent data:', {
            registeredByParent: (player as any).registeredByParent,
            parentEmail: (player as any).parentEmail,
            parentPhone: (player as any).parentPhone,
            fatherName: (player as any).fatherName,
            motherName: (player as any).motherName,
            parentCountry: (player as any).parentCountry,
            hasParentData
          });
          return hasParentData;
        })() && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon />
                  Parent/Guardian Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {(player as any).fatherName && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">Father Name</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                        {(player as any).fatherName}
                      </Typography>
                    </Box>
                  )}
                  
                  {(player as any).motherName && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">Mother Name</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                        {(player as any).motherName}
                      </Typography>
                    </Box>
                  )}
                  
                  {(player as any).parentEmail && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">Parent Email</Typography>
                      <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EmailIcon fontSize="small" />
                        {(player as any).parentEmail}
                      </Typography>
                    </Box>
                  )}
                  
                  {(player as any).parentPhone && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">Parent Phone</Typography>
                      <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PhoneIcon fontSize="small" />
                        {(player as any).parentPhone}
                      </Typography>
                    </Box>
                  )}
                  
                  {(player as any).parentCountry && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">Parent Country</Typography>
                      <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocationIcon fontSize="small" />
                        {(player as any).parentCountry}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Account Information */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Account Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Account Status</Typography>
                    <Chip 
                      label={player.user?.isActive !== false ? 'Active' : 'Inactive'} 
                      color={player.user?.isActive !== false ? 'success' : 'error'} 
                      size="small"
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Role</Typography>
                    <Chip 
                      label={player.user?.role ? String(player.user.role).charAt(0).toUpperCase() + String(player.user.role).slice(1) : 'Player'} 
                      color="primary" 
                      size="small"
                    />
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* OTP Verification Dialog */}
      <Dialog open={otpDialogOpen} onClose={() => setOtpDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Verify Phone Number</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Enter the 6-digit OTP code sent to your phone number
            </Typography>
            <TextField
              label="OTP Code"
              fullWidth
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputProps={{ maxLength: 6, style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem' } }}
              placeholder="000000"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOtpDialogOpen(false); setOtpCode(''); }}>
            Cancel
          </Button>
          <Button
            onClick={handleVerifyOTP}
            variant="contained"
            disabled={otpCode.length !== 6 || verifyingOTP}
          >
            {verifyingOTP ? 'Verifying...' : 'Verify'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PlayerProfile;
